import React, { ReactNode } from "react";
import { Col, Form, Row } from "react-bootstrap";
import CustomButton from "shared/components/button/CustomButton";
import CustomModal from "shared/components/modal/CustomModal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TipoCanalComunicacao } from "constants/EnumTipoCanalComunicacao";
import { SelectOptionType } from "types/select/SelectOptionType";
import CustomSelect from "shared/components/select/CustomSelect";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import CustomTextArea from "shared/components/input/CustomTextArea";
import TextInput from "shared/components/input/TextInput";
import { TipoMotivoInadimplencia } from "constants/EnumTipoMotivoInadimplencia";
import { TipoResultadoContato } from "constants/EnumTipoResultadoContato";
import { CustomTimePicker } from "shared/components/date/CustomTimePicker";
import { formatarDateParaLocalTime } from "shared/util/functions/formatacoesDeValores/FormatarValores";
import registrarContatoRealizadoSchema from "shared/yup/schemas/RegistrarContatoRealizadoSchema";
import { useGestaoCobrancaCadastroContext } from "context/gestaoCobranca/GestaoCobrancaCadastroContext";
import { usePostContatosRealizados } from "hooks/gestaoCobranca/usePostContatosRealizados";
import { ContatosRealizadosDTO } from "types/dto/GestaoCobrancaContatoDTO";
import { toast } from "react-hot-toast";
import showAlert from "shared/util/functions/alert/showAlert";

interface ModalRegistrarContatoRealizadoProps {
  showModal: boolean;
  onCloseModal: () => void;
}

interface RegistrarContatoRealizadoForm {
  contatoRealizadoCom: string;
  dataContato: Date;
  horarioContato: Date;
  canalComunicacao: TipoCanalComunicacao | undefined;
  resultadoContato: TipoResultadoContato;
  motivoInadimplencia: TipoMotivoInadimplencia;
  anotacoes: string | undefined;
}

const ModalRegistrarContatoRealizado: React.FC<ModalRegistrarContatoRealizadoProps> = (props) => {
  const { unidadeId } = useGestaoCobrancaCadastroContext();

  const { mutateAsync: cadastrarContatoRealizado } = usePostContatosRealizados();

  const { register, handleSubmit, reset, control, formState, setValue } = useForm<RegistrarContatoRealizadoForm>({
    resolver: yupResolver<RegistrarContatoRealizadoForm>(registrarContatoRealizadoSchema),
  });

  const opcoesCanalDeComunicacao: SelectOptionType<TipoCanalComunicacao>[] = [
    {
      label: "Telefone",
      value: TipoCanalComunicacao.TELEFONE,
    },
    {
      label: "E-mail",
      value: TipoCanalComunicacao.EMAIL,
    },
    {
      label: "SMS",
      value: TipoCanalComunicacao.SMS,
    },
    {
      label: "Carta",
      value: TipoCanalComunicacao.CARTA,
    },
    {
      label: "Outros",
      value: TipoCanalComunicacao.OUTROS,
    },
  ];

  const opcoesResultadoDoContato: SelectOptionType<TipoResultadoContato>[] = [
    {
      label: "Acordo",
      value: TipoResultadoContato.ACORDO,
    },
    {
      label: "Já pagou",
      value: TipoResultadoContato.JA_PAGOU,
    },
    {
      label: "Promessa",
      value: TipoResultadoContato.PROMESSA,
    },
    {
      label: "Sem contato",
      value: TipoResultadoContato.SEM_CONTATO,
    },
  ];

  const opcoesMotivoInadimplencia: SelectOptionType<TipoMotivoInadimplencia>[] = [
    {
      label: "Problemas Financeiros",
      value: TipoMotivoInadimplencia.PROBLEMAS_FINANCEIROS,
    },
    {
      label: "Não concorda com a cobrança",
      value: TipoMotivoInadimplencia.NAO_CONCORDA_COBRANCA,
    },
    {
      label: "Não recebeu o boleto",
      value: TipoMotivoInadimplencia.NAO_RECEBEU_BOLETO,
    },
    {
      label: "Sem contato",
      value: TipoMotivoInadimplencia.SEM_CONTATO,
    },
  ];

  const onSubmitRegistrarContatoRealizado = async (registrarContatoForm: RegistrarContatoRealizadoForm) => {
    const contatosRealizadosDTO = {
      ...registrarContatoForm,
      unidadeId,
      dataContato: registrarContatoForm.dataContato.toISOString(),
      horarioContato: formatarDateParaLocalTime(registrarContatoForm.horarioContato),
    } as ContatosRealizadosDTO;

    try {
      await cadastrarContatoRealizado(contatosRealizadosDTO);
      reset();
      props.onCloseModal();
    } catch (error: any) {
      if (error?.response?.data?.validation[0]?.message) {
        toast.error(error.response.data.validation[0].message);
      } else {
        toast.error("Erro ao salvar registro.");
      }
    }
  };

  const renderFooterModalRegistrarContato = (): ReactNode => {
    return (
      <Row>
        <Col className="d-flex justify-content-end p-0">
          <CustomButton
            id="btn-cancelar-modal-registrar-contato"
            label="Cancelar"
            onClick={() => closeModal()}
            variant="danger-alt"
            className="me-2"
          />
          <CustomButton
            id="btn-cancelar-modal-registrar-contato"
            label="Salvar"
            onClick={handleSubmit(onSubmitRegistrarContatoRealizado)}
            variant="primary"
            className="ps-4 pe-4"

          />
        </Col>
      </Row>
    );
  };

  const renderBodyModalRegistrarContatoRealizado = (): ReactNode => {
    return (
      <div>
        <span>
          Registre aqui o contato realizado com o responsável da unidade, lembre-se de adicionar as informações durante
          o contato para registro
        </span>
        <Form.Group>
          <Row>{renderContatoRealizadoCom()}</Row>
          <Row>
            {renderDataDoContato()}
            {renderHorarioContato()}
          </Row>
          <Row>
            {renderCanalDeComunicacao()}
            {renderResultadoDoContato()}
          </Row>
          <Row>{renderMotivoInadimplencia()}</Row>
          <Row>{renderAnotacoes()}</Row>
        </Form.Group>
      </div>
    );
  };

  const renderContatoRealizadoCom = (): ReactNode => {
    return (
      <Col xs={8}>
        <TextInput
          id="contato-realizado-com"
          className="mt-4"
          label="Contato realizado com"
          usarValidacaoParaFormulario={true}
          name="contatoRealizadoCom"
          register={register}
          control={control}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderDataDoContato = (): ReactNode => {
    return (
      <Col className="mt-3" xs={4} lg ={4} xl={4} xxl={4}>
        <CustomDatePicker
          usarValidacaoParaFormulario
          name="dataContato"
          control={control}
          register={register}
          setValue={setValue}
          disabledFuture
          label="Data do contato"
        />
      </Col>
    );
  };

  const renderHorarioContato = (): ReactNode => {
    return (
      <Col className="mt-3" xs={4} lg ={4} xl={4} xxl={4}>
        <CustomTimePicker
          usarValidacaoParaFormulario
          label="Horário"
          name="horarioContato"
          control={control}
          register={register}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderCanalDeComunicacao = (): ReactNode => {
    return (
      <Col className="mt-3" xs={4}>
        <CustomSelect
          id="canal-de-comunicacao"
          label="Canal de comunicação"
          usarValidacaoParaFormulario
          name="canalComunicacao"
          register={register}
          control={control}
          options={opcoesCanalDeComunicacao}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderResultadoDoContato = (): ReactNode => {
    return (
      <Col className="mt-3" xs={4}>
        <CustomSelect
          id="resultado-do-contato"
          label="Resultado do contato"
          usarValidacaoParaFormulario
          name="resultadoContato"
          control={control}
          register={register}
          options={opcoesResultadoDoContato}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderMotivoInadimplencia = (): ReactNode => {
    return (
      <Col className="mt-3" xs={8}>
        <CustomSelect
          usarValidacaoParaFormulario
          id="motivo-inadimplencia"
          label="Motivo da inadimplência"
          options={opcoesMotivoInadimplencia}
          control={control}
          register={register}
          name="motivoInadimplencia"
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderAnotacoes = (): ReactNode => {
    return (
      <Col xs={8}>
        <CustomTextArea
          id="anotacoes-contato"
          usarValidacaoParaFormulario
          className="mt-4"
          label="Anotações"
          direcaoExpansao="both"
          control={control}
          register={register}
          setValue={setValue}
          name="anotacoes"
        />
      </Col>
    );
  };

  const closeModal = () => {
    const fecharModal = () => {
      reset();
      props.onCloseModal();
    };
    if (formState.isDirty) {
      showAlert({
        texto: "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => fecharModal(),
      });
    } else {
      fecharModal();
    }
  };

  return (
    <div>
      <CustomModal
        titulo="Registrar contato"
        onCloseModal={() => closeModal()}
        showModal={props.showModal}
        fecharModalAoClicarFora={!formState.isDirty}
        size="lg"
        tipoModal="MODAL_LATERAL"
        customFooter={renderFooterModalRegistrarContato()}
        scrollable
      >
        {renderBodyModalRegistrarContatoRealizado()}
      </CustomModal>
    </div>
  );
};

export default ModalRegistrarContatoRealizado;
